import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { MessageSquare, Send, ArrowLeft, Loader2, Home, User, Briefcase } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ctxIcon = { rental: Home, professional: User, job: Briefcase };
const ctxLabel = { rental: "Imóvel", professional: "Profissional", job: "Vaga" };
const fmtTime = (s) => (s ? new Date(s).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "");

export function MessagesTab({ onRead }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadConvs = useCallback(async () => {
    try {
      const { data } = await api.get("/me/conversations");
      setConvs(data);
    } catch { /* ignore */ }
    finally { setLoadingList(false); }
  }, []);

  const openConv = useCallback(async (cid) => {
    setActive(cid);
    setLoadingMsgs(true);
    try {
      const { data } = await api.get(`/conversations/${cid}/messages`);
      setMessages(data.messages);
      setConvs((prev) => prev.map((c) => (c.id === cid ? { ...c, unread: 0 } : c)));
      onRead?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoadingMsgs(false);
    }
  }, [onRead]);

  useEffect(() => { loadConvs(); }, [loadConvs]);

  useEffect(() => {
    const cid = params.get("c");
    if (cid) openConv(cid);
  }, [params, openConv]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Poll for new messages / conversations
  useEffect(() => {
    const t = setInterval(() => {
      loadConvs();
      if (active) api.get(`/conversations/${active}/messages`).then(({ data }) => setMessages(data.messages)).catch(() => {});
    }, 8000);
    return () => clearInterval(t);
  }, [active, loadConvs]);

  const activeConv = convs.find((c) => c.id === active);

  const send = async () => {
    const body = text.trim();
    if (!body || !active) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${active}/messages`, { body });
      setMessages((prev) => [...prev, data]);
      setText("");
      loadConvs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const goBack = () => { setActive(null); setMessages([]); setParams({ tab: "messages" }); };

  return (
    <div className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm overflow-hidden" data-testid="messages-tab">
      <div className="grid md:grid-cols-3 min-h-[520px]">
        {/* Conversation list */}
        <div className={`md:col-span-1 border-r border-[#e7e5e4] ${active ? "hidden md:block" : "block"}`}>
          <div className="px-5 py-4 border-b border-[#e7e5e4] font-head font-semibold text-[#1c1917] flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#1b5e3f]" /> Conversas
          </div>
          {loadingList ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#1b5e3f]" /></div>
          ) : convs.length === 0 ? (
            <div className="p-6 text-sm text-[#78716c]">
              <p className="font-medium text-[#1c1917]">Nenhuma conversa ainda</p>
              <p className="mt-1">Inicie uma conversa contatando um anúncio no diretório, aluguéis ou ofertas de trabalho.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#e7e5e4] overflow-y-auto max-h-[520px]">
              {convs.map((c) => {
                const Icon = ctxIcon[c.context_type] || MessageSquare;
                return (
                  <button
                    key={c.id}
                    data-testid={`conv-item-${c.id}`}
                    onClick={() => setParams({ tab: "messages", c: c.id })}
                    className={`w-full text-left px-5 py-4 hover:bg-[#fafaf8] transition-colors ${active === c.id ? "bg-[#e8f0ec]" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-[#1c1917] truncate">{c.other_user?.name}</span>
                      {c.unread > 0 && <span data-testid={`conv-unread-${c.id}`} className="shrink-0 text-[10px] font-bold bg-[#1b5e3f] text-white rounded-full min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center">{c.unread}</span>}
                    </div>
                    <div className="text-xs text-[#78716c] flex items-center gap-1 mt-0.5"><Icon className="w-3 h-3" /> {ctxLabel[c.context_type]}: {c.context_title}</div>
                    {c.last_message && <p className="text-xs text-[#78716c] truncate mt-1">{c.last_message}</p>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Message thread */}
        <div className={`md:col-span-2 flex flex-col ${active ? "flex" : "hidden md:flex"}`}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#78716c] p-8 text-center">
              Selecione uma conversa para ver as mensagens.
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-[#e7e5e4] flex items-center gap-3">
                <button onClick={goBack} className="md:hidden text-[#78716c]"><ArrowLeft className="w-5 h-5" /></button>
                <div className="min-w-0">
                  <p className="font-head font-semibold text-[#1c1917] truncate" data-testid="active-conv-name">{activeConv?.other_user?.name || "Conversa"}</p>
                  {activeConv && <p className="text-xs text-[#78716c] truncate">{ctxLabel[activeConv.context_type]}: {activeConv.context_title}</p>}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-[#fafaf8] max-h-[420px]">
                {loadingMsgs ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#1b5e3f]" /></div>
                ) : messages.length === 0 ? (
                  <p className="text-sm text-[#78716c] text-center py-8">Nenhuma mensagem. Escreva a primeira!</p>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_id === user.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div data-testid={`msg-${m.id}`} className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-[#1b5e3f] text-white rounded-br-md" : "bg-white border border-[#e7e5e4] text-[#1c1917] rounded-bl-md"}`}>
                          <p className="whitespace-pre-line break-words">{m.body}</p>
                          <p className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[#78716c]"}`}>{fmtTime(m.created_at)}</p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={endRef} />
              </div>

              <div className="p-4 border-t border-[#e7e5e4] flex items-end gap-2">
                <textarea
                  data-testid="msg-input"
                  rows={1}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Escreva uma mensagem..."
                  className="flex-1 resize-none bg-white border border-[#e7e5e4] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b5e3f]/20 focus:border-[#1b5e3f]"
                />
                <Button data-testid="msg-send-btn" onClick={send} disabled={sending || !text.trim()} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c] shrink-0">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
